import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api, { aiCategorizeApi } from '../utils/axios';
import { format, parseISO, isValid } from 'date-fns';
import {
  MdArrowUpward, MdArrowDownward, MdDelete, MdAutoAwesome,
  MdFileDownload, MdSearch
} from 'react-icons/md';
import './Transactions.css';

const fmtCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(parseFloat(n) || 0));

const fmtSigned = (n) => {
  const v = parseFloat(n) || 0;
  return (v >= 0 ? '+' : '-') + fmtCurrency(v);
};

const todayYm = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category_id: '', month: todayYm(), search: '' });
  const [sortConfig, setSortConfig] = useState({ key: 'transaction_date', direction: 'desc' });
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchData = useCallback(async () => {
    try {
      const params = { category_id: filters.category_id };
      if (filters.month) {
        const [year, month] = filters.month.split('-');
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        params.start_date = `${year}-${month}-01`;
        params.end_date = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
      }

      const [transactionsRes, categoriesRes] = await Promise.all([
        api.get('/api/transactions', { params }),
        api.get('/api/categories'),
      ]);
      setTransactions(transactionsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setMessage({ type: 'error', text: 'Failed to load transactions' });
    } finally {
      setLoading(false);
    }
  }, [filters.category_id, filters.month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getCategoryById = (id) => categories.find((c) => c.id === id);

  const handleCategoryChange = async (transactionId, categoryId) => {
    try {
      await api.put(`/api/transactions/${transactionId}/category`, {
        category_id: categoryId || null,
      });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update category' });
    }
  };

  const [aiBusy, setAiBusy] = useState(false);
  // Bumped on every AI categorize click so a stale poll loop knows to stop
  // when the user clicks again before the previous one finishes.
  const aiSessionRef = useRef(0);

  const handleAiCategorize = async () => {
    const mySession = ++aiSessionRef.current;
    setAiBusy(true);
    // Scope the run to the currently-viewed month (matches what the user sees on the page).
    // If no month is selected, fall back to processing everything.
    let body = {};
    let scopeLabel = 'all transactions';
    let params = {};
    if (filters.month) {
      const [year, month] = filters.month.split('-');
      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
      body = {
        start_date: `${year}-${month}-01`,
        end_date: `${year}-${month}-${String(lastDay).padStart(2, '0')}`,
      };
      params = { start_date: body.start_date, end_date: body.end_date };
      scopeLabel = `${filters.month}`;
    }

    // Baseline: count uncategorized transactions in the current scope BEFORE
    // we run AI. When this number drops, the backend has finished writing
    // categorizations - regardless of whether the HTTP response made it back.
    const countUncategorized = (rows) =>
      rows.filter((t) => !t.category_id).length;
    const baselineUncategorized = countUncategorized(transactions);

    setMessage({ type: 'success', text: `Running AI categorization for ${scopeLabel}…` });

    try {
      // aiCategorizeApi routes through the long-timeout Lambda Function URL
      // in production. Locally it falls through to the same Express server.
      //
      // We deliberately use a tight 35s timeout instead of axios's default 120s
      // because the Lambda Function URL is fronted by CloudFront, which has a
      // hard 30s origin response timeout. If we wait the full 120s we just
      // hang the UI long after the request is already dead - better to bail
      // fast and switch to polling DDB for actual completion.
      const response = await aiCategorizeApi.post('/api/transactions/ai-categorize', body, {
        timeout: 35000,
      });
      if (mySession !== aiSessionRef.current) return; // user clicked again, drop result
      setMessage({ type: 'success', text: response.data.message });
      fetchData();
      setAiBusy(false);
    } catch (error) {
      // If the backend gave us a real JSON error (e.g. "Anthropic API key not
      // configured"), surface it - that's a real bug to fix, not a timeout.
      if (error.response?.data?.error) {
        if (mySession !== aiSessionRef.current) return;
        setMessage({ type: 'error', text: error.response.data.error });
        setAiBusy(false);
        return;
      }

      // No JSON body = transport-layer failure. Most likely the CloudFront
      // 30s timeout fired while the Lambda kept running. Poll DDB until the
      // backend's writes appear, then refresh.
      setMessage({
        type: 'success',
        text: 'Still working… AI is taking a bit. Watching for results…',
      });

      const POLL_INTERVAL_MS = 5000;
      const MAX_POLLS = 36; // 3 minutes
      let pollsLeft = MAX_POLLS;

      const tick = async () => {
        if (mySession !== aiSessionRef.current) return; // newer session in progress
        if (pollsLeft-- <= 0) {
          setMessage({
            type: 'error',
            text: 'AI categorize is taking unusually long. Refresh the page in a minute to check.',
          });
          setAiBusy(false);
          return;
        }
        try {
          const r = await api.get('/api/transactions', { params });
          if (mySession !== aiSessionRef.current) return;
          const nowUncat = countUncategorized(r.data);
          if (nowUncat < baselineUncategorized) {
            // Progress! AI has written categories. We're done.
            const applied = baselineUncategorized - nowUncat;
            setTransactions(r.data);
            setMessage({
              type: 'success',
              text: `AI categorized ${applied} transactions for ${scopeLabel}.`,
            });
            setAiBusy(false);
            return;
          }
        } catch (e) {
          // Network blip - just try again next tick.
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      };
      setTimeout(tick, POLL_INTERVAL_MS);
    }
  };

  const handleDelete = async (transactionId, label) => {
    if (!window.confirm(`Delete this transaction?\n\n${label || 'Transaction'}`)) return;
    try {
      await api.delete(`/api/transactions/${transactionId}`);
      setMessage({ type: 'success', text: 'Transaction deleted' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete' });
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const renderSortIcon = (col) =>
    sortConfig.key === col
      ? sortConfig.direction === 'asc'
        ? <MdArrowUpward className="sort-icon" />
        : <MdArrowDownward className="sort-icon" />
      : null;

  // Filter + sort
  const visible = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    const filtered = transactions.filter((t) => {
      if (!term) return true;
      const fields = [
        t.merchant_name,
        t.description,
        t.category_name,
        t.institution_name,
        t.account_name,
      ].filter(Boolean).join(' ').toLowerCase();
      return fields.includes(term);
    });

    return [...filtered].sort((a, b) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      if (sortConfig.key === 'transaction_date') {
        return dir * (new Date(a.transaction_date) - new Date(b.transaction_date));
      }
      if (sortConfig.key === 'category') {
        const av = getCategoryById(a.category_id)?.name || 'Uncategorized';
        const bv = getCategoryById(b.category_id)?.name || 'Uncategorized';
        return dir * av.localeCompare(bv);
      }
      if (sortConfig.key === 'amount') {
        return dir * (Math.abs(parseFloat(a.amount)) - Math.abs(parseFloat(b.amount)));
      }
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, sortConfig, filters.search, categories]);

  // Group by date for display (only when sorted by date)
  const grouped = useMemo(() => {
    if (sortConfig.key !== 'transaction_date') return [{ date: null, items: visible }];
    const map = new Map();
    visible.forEach((t) => {
      const d = format(new Date(t.transaction_date), 'yyyy-MM-dd');
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(t);
    });
    return [...map.entries()].map(([date, items]) => ({ date, items }));
  }, [visible, sortConfig.key]);

  // Summary numbers
  const summary = useMemo(() => {
    // Transfers are visible but don't count toward income / spending totals.
    const active = visible.filter((t) => t.category_type !== 'transfer');
    if (active.length === 0) {
      return { count: 0, largest: 0, average: 0, income: 0, spending: 0, first: null, last: null };
    }
    const amounts = active.map((t) => parseFloat(t.amount) || 0);
    const income = amounts.filter((a) => a > 0).reduce((s, a) => s + a, 0);
    const spending = amounts.filter((a) => a < 0).reduce((s, a) => s + Math.abs(a), 0);
    const largestSigned = amounts.reduce((max, a) => (Math.abs(a) > Math.abs(max) ? a : max), 0);
    const average = (income + spending) / active.length;
    const dates = active.map((t) => new Date(t.transaction_date)).sort((a, b) => a - b);
    return {
      count: active.length,
      largest: largestSigned,
      average,
      income,
      spending,
      first: dates[0],
      last: dates[dates.length - 1],
    };
  }, [visible]);

  const downloadCsv = () => {
    const rows = [
      ['Date', 'Merchant', 'Description', 'Category', 'Account', 'Amount'],
      ...visible.map((t) => [
        format(new Date(t.transaction_date), 'yyyy-MM-dd'),
        (t.merchant_name || '').replace(/"/g, '""'),
        (t.description || '').replace(/"/g, '""'),
        (getCategoryById(t.category_id)?.name || ''),
        (t.account_name || ''),
        parseFloat(t.amount).toFixed(2),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${filters.month || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="loading">Loading transactions...</div>;

  return (
    <div className="tx-page">
      {message.text && <div className={message.type}>{message.text}</div>}

      <div className="tx-toolbar">
        <div className="tx-search">
          <MdSearch className="tx-search-icon" />
          <input
            className="tx-search-input"
            placeholder="Search merchant, category, account…"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
        <div className="tx-toolbar-controls">
          <input
            type="month"
            className="input tx-month"
            value={filters.month}
            onChange={(e) => setFilters({ ...filters, month: e.target.value })}
          />
          <select
            className="input tx-category"
            value={filters.category_id}
            onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="tx-body">
        <div className="tx-list-wrap">
          <div className="card tx-list-card">
            <div className="tx-list-head">
              <div className="tx-list-title">Transactions</div>
              <div className="tx-list-actions">
                <button
                  className="button"
                  onClick={handleAiCategorize}
                  disabled={aiBusy}
                  title="Use Claude to categorize uncategorized transactions and detect transfers"
                >
                  <MdAutoAwesome /> {aiBusy ? 'AI working…' : 'AI Categorize'}
                </button>
              </div>
            </div>

            <div className="tx-column-head">
              <div className="col-date sortable" onClick={() => handleSort('transaction_date')}>
                Date {renderSortIcon('transaction_date')}
              </div>
              <div className="col-merchant">Merchant</div>
              <div className="col-category sortable" onClick={() => handleSort('category')}>
                Category {renderSortIcon('category')}
              </div>
              <div className="col-account">Account</div>
              <div className="col-amount sortable" onClick={() => handleSort('amount')}>
                Amount {renderSortIcon('amount')}
              </div>
              <div className="col-actions"></div>
            </div>

            {visible.length === 0 ? (
              <p className="empty-state">
                No transactions for this filter. Try a different month or clear the search.
              </p>
            ) : (
              grouped.map(({ date, items }) => {
                const dayTotal = items.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
                const parsed = date ? parseISO(date) : null;
                return (
                  <div key={date || 'all'} className="tx-day-group">
                    {date && (
                      <div className="tx-day-header">
                        <span>{isValid(parsed) ? format(parsed, 'MMM d, yyyy') : date}</span>
                        <span className={dayTotal >= 0 ? 'pos' : 'neg'}>
                          {fmtSigned(dayTotal)}
                        </span>
                      </div>
                    )}
                    {items.map((t) => {
                      const cat = getCategoryById(t.category_id);
                      const expense = parseFloat(t.amount) < 0;
                      return (
                        <div key={t.id} className="tx-row">
                          <div className="col-date">{format(new Date(t.transaction_date), 'MMM d')}</div>
                          <div className="col-merchant">
                            <div
                              className="tx-merchant-icon"
                              style={{ background: (cat?.color || '#cccccc') + '22', color: cat?.color || '#6b6b6b' }}
                            >
                              {cat?.icon || '•'}
                            </div>
                            <div className="tx-merchant-text">
                              <div className="tx-merchant-name">
                                {t.merchant_name || t.description || 'Transaction'}
                              </div>
                              {t.pending && <span className="badge pending">Pending</span>}
                            </div>
                          </div>
                          <div className="col-category">
                            <select
                              className="tx-cat-select"
                              value={t.category_id || ''}
                              onChange={(e) => handleCategoryChange(t.id, e.target.value)}
                              style={{ borderLeftColor: cat?.color || '#d1d5db' }}
                            >
                              <option value="">Uncategorized</option>
                              {categories.map((c) => (
                                <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-account">{t.account_name || '—'}</div>
                          <div className={`col-amount ${expense ? 'neg' : 'pos'}`}>
                            {fmtSigned(t.amount)}
                          </div>
                          <div className="col-actions">
                            <button
                              className="icon-button danger"
                              onClick={() => handleDelete(t.id, t.merchant_name || t.description)}
                              title="Delete"
                            >
                              <MdDelete />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <aside className="tx-summary card">
          <h3 className="summary-title">Summary</h3>
          <div className="summary-row">
            <span className="summary-label">Total transactions</span>
            <span className="summary-value">{summary.count.toLocaleString()}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Largest transaction</span>
            <span className={`summary-value ${summary.largest >= 0 ? 'pos' : ''}`}>
              {summary.largest === 0 ? '—' : fmtSigned(summary.largest)}
            </span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Average transaction</span>
            <span className="summary-value">{summary.count ? fmtCurrency(summary.average) : '—'}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Total income</span>
            <span className="summary-value pos">{summary.income ? '+' + fmtCurrency(summary.income) : '—'}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Total spending</span>
            <span className="summary-value">{summary.spending ? fmtCurrency(summary.spending) : '—'}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">First transaction</span>
            <span className="summary-value">{summary.first ? format(summary.first, 'MMM d, yyyy') : '—'}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Last transaction</span>
            <span className="summary-value">{summary.last ? format(summary.last, 'MMM d, yyyy') : '—'}</span>
          </div>

          <button className="summary-csv" onClick={downloadCsv} disabled={visible.length === 0}>
            <MdFileDownload /> Download CSV
          </button>
        </aside>
      </div>
    </div>
  );
};

export default Transactions;
